import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Accessibility,
  AlignLeft,
  CaseSensitive,
  Contrast,
  Eye,
  ImageOff,
  Link,
  MousePointer2,
  PauseCircle,
  RotateCcw,
  Rows3,
  ScanLine,
  Space,
  X,
} from 'lucide-react';

type AccessibilitySetting =
  | 'contrast'
  | 'highlightLinks'
  | 'biggerText'
  | 'textSpacing'
  | 'pauseAnimations'
  | 'hideImages'
  | 'readableFont'
  | 'largeCursor'
  | 'lineHeight'
  | 'textAlign'
  | 'saturation'
  | 'focusMode';

const STORAGE_KEY = 'inspire-accessibility-settings';

const defaultSettings: Record<AccessibilitySetting, boolean> = {
  contrast: false,
  highlightLinks: false,
  biggerText: false,
  textSpacing: false,
  pauseAnimations: false,
  hideImages: false,
  readableFont: false,
  largeCursor: false,
  lineHeight: false,
  textAlign: false,
  saturation: false,
  focusMode: false,
};

const controls = [
  { key: 'contrast', label: 'Contrast +', icon: Contrast },
  { key: 'highlightLinks', label: 'Highlight Links', icon: Link },
  { key: 'biggerText', label: 'Bigger Text', icon: CaseSensitive },
  { key: 'textSpacing', label: 'Text Spacing', icon: Space },
  { key: 'pauseAnimations', label: 'Pause Animations', icon: PauseCircle },
  { key: 'hideImages', label: 'Hide Images', icon: ImageOff },
  { key: 'readableFont', label: 'Readable Font', icon: Eye },
  { key: 'largeCursor', label: 'Large Cursor', icon: MousePointer2 },
  { key: 'lineHeight', label: 'Line Height', icon: Rows3 },
  { key: 'textAlign', label: 'Text Align', icon: AlignLeft },
  { key: 'saturation', label: 'Saturation', icon: ScanLine },
] satisfies Array<{ key: AccessibilitySetting; label: string; icon: typeof Accessibility }>;

function loadSettings(): Record<AccessibilitySetting, boolean> {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) return defaultSettings;
    return { ...defaultSettings, ...JSON.parse(saved) };
  } catch {
    return defaultSettings;
  }
}

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Record<AccessibilitySetting, boolean>>(() => loadSettings());
  const { pathname } = useLocation();
  const onPropertyPage = [
    '/the-belvedere',
    '/hannahs-pearl',
    '/sea-dreams',
    '/beach-beach-baby',
    '/bella-vita',
    '/sunset-bungalow-30a',
  ].includes(pathname);

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(settings).forEach(([key, active]) => {
      root.toggleAttribute(`data-a11y-${key.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)}`, active);
    });
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const toggle = (key: AccessibilitySetting) => {
    setSettings((current) => ({ ...current, [key]: !current[key] }));
  };

  const reset = () => setSettings(defaultSettings);

  return (
    <div className={`fixed left-5 ${onPropertyPage ? 'bottom-24 sm:bottom-28' : 'bottom-5'} z-[70]`}>
      {open && (
        <div className="mb-4 w-[390px] max-w-[calc(100vw-2.5rem)] max-h-[min(720px,calc(100vh-7.5rem))] overflow-hidden rounded-2xl bg-[#EEF2F7] text-[#071018] shadow-2xl border border-white/70">
          <div className="bg-[#2F66F6] text-white px-5 py-4 flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold leading-tight">Accessibility Menu</p>
              <p className="text-xs uppercase tracking-[0.16em] text-white/80">Customize display</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close accessibility options"
              className="w-10 h-10 rounded-full border-2 border-white/65 bg-white/15 flex items-center justify-center hover:bg-white/25 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="max-h-[calc(min(720px,100vh-7.5rem)-72px)] overflow-y-auto subtle-scroll p-4">
            <button
              type="button"
              onClick={() => toggle('focusMode')}
              aria-pressed={settings.focusMode}
              className={`mb-4 w-full min-h-14 rounded-xl px-4 flex items-center justify-center gap-3 text-base font-semibold transition-colors ${
                settings.focusMode ? 'bg-[#071018] text-white' : 'bg-[#2F66F6] text-white hover:bg-[#2557DE]'
              }`}
            >
              <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Eye size={18} />
              </span>
              Focus Reader
            </button>

            <div className="grid grid-cols-2 gap-3">
              {controls.map(({ key, label, icon: Icon }) => {
                const active = settings[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggle(key)}
                    aria-pressed={active}
                    className={`min-h-[118px] rounded-xl border px-3 py-4 flex flex-col items-center justify-center text-center gap-3 transition-all ${
                      active
                        ? 'bg-[#071018] text-white border-[#071018] shadow-md'
                        : 'bg-white text-[#071018] border-white hover:border-[#2F66F6] hover:shadow-sm'
                    }`}
                  >
                    <Icon size={34} strokeWidth={2.2} />
                    <span className="text-[15px] font-semibold leading-tight">{label}</span>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={reset}
              className="mt-4 w-full rounded-xl bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.14em] text-[#2F66F6] hover:bg-[#E7ECF5] transition-colors flex items-center justify-center gap-2"
            >
              <RotateCcw size={16} />
              Reset All
            </button>
            <a
              href="/accessibility-statement"
              className="mt-3 flex items-center justify-center text-sm font-semibold text-[#2F66F6] underline underline-offset-4 hover:text-[#071018]"
            >
              Accessibility Statement
            </a>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-label="Open accessibility options"
        className="w-16 h-16 rounded-full bg-[#071018] text-white border-2 border-white shadow-2xl flex items-center justify-center hover:bg-[#2F66F6] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#2F66F6]/40 transition-colors"
      >
        <Accessibility size={38} strokeWidth={2.4} aria-hidden="true" />
      </button>
    </div>
  );
}
